import { useMemo, useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Shield, ShieldAlert, ShieldOff, ShieldCheck } from "lucide-react";

// Phase 2Z.n - Coverage Guard V1 tri-state badge.
//
// Renders exactly ONE icon. No banners, no toasts, no panels. The
// click-popover is the only place that explains *what* is
// under-covered (or *why* the evaluator couldn't fully check).
//
// Props:
//   verdict: { overall: "green" | "red" | "grey",
//              perRule, unsupportedRulesCount, totalEvaluatedCount }
//   onOpenSettings: callback for the "Manage Coverage Guard" link.
//   size: "sm" (header) | "xs" (inside RMI drawer)
//   dayLabels: optional array (used for Rota Style cycle day labels)
//   testid: a stable test id for this surface (e.g.
//           "coverage-guard-badge-rota-style").

const TONE = {
  green: {
    Icon: ShieldCheck,
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    label: "Covered",
    sub: "All Coverage Guard rules look good",
  },
  red: {
    Icon: ShieldAlert,
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
    label: "Needs attention",
    sub: "Coverage Guard found an issue",
  },
  grey: {
    Icon: ShieldOff,
    bg: "bg-slate-50",
    text: "text-slate-500",
    border: "border-slate-200",
    label: "Not configured",
    sub: "No Coverage Guard rules - click to set up",
  },
};

const ROW_TONE = {
  ok: { bg: "bg-emerald-50", text: "text-emerald-700",
        border: "border-emerald-200", label: "Covered" },
  risk: { bg: "bg-amber-50", text: "text-amber-700",
          border: "border-amber-200", label: "At risk" },
  under: { bg: "bg-rose-50", text: "text-rose-700",
            border: "border-rose-200", label: "Under-covered" },
  "not-fully-checked": {
    bg: "bg-slate-100", text: "text-slate-600",
    border: "border-slate-200", label: "Not fully checked",
  },
};

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CoverageGuardBadge({
  verdict, onOpenSettings, size = "sm", dayLabels, testid,
  emptyHint, hidden,
}) {
  const tone = TONE[verdict?.overall || "grey"];
  const Icon = tone.Icon;
  const [open, setOpen] = useState(false);
  const isXs = size === "xs";
  const isMd = size === "md";

  const heading = useMemo(() => {
    if (verdict?.overall === "green") return "Coverage Guard: looks good";
    if (verdict?.overall === "red") return "Coverage Guard: needs attention";
    return "Coverage Guard";
  }, [verdict?.overall]);

  // Phase 2Z.aa: paid Starter trim - hide the badge entirely. Placed
  // AFTER all hook calls so React Hooks rules-of-hooks holds.
  if (hidden) return null;

  // Size tier: xs (inside RMI drawer) | sm (legacy) | md (premium,
  // alongside main controls like the Rota Style team filter).
  const trigger = (() => {
    if (isXs) return {
      pad: "px-2 py-0.5 text-[10px]",
      icon: "h-3 w-3",
      countBadge: "h-4 min-w-4 text-[9px]",
    };
    if (isMd) return {
      pad: "px-3 py-1.5 text-[12px]",
      icon: "h-4 w-4",
      countBadge: "h-5 min-w-5 text-[10px]",
    };
    return {
      pad: "px-2.5 py-1 text-xs",
      icon: "h-3.5 w-3.5",
      countBadge: "h-4 min-w-4 text-[9px]",
    };
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid || "coverage-guard-badge"}
          data-cg-state={verdict?.overall || "grey"}
          className={`inline-flex items-center gap-1.5 rounded-full border transition-all ${tone.bg} ${tone.text} ${tone.border} ${trigger.pad} font-medium shadow-sm hover:shadow ${
            isMd ? "ring-1 ring-inset ring-white/40" : ""
          }`}
          aria-label={`Coverage Guard - ${tone.label}`}
        >
          <Shield
            className={trigger.icon}
            style={{ display: "none" }}
          />
          <Icon className={trigger.icon} />
          <span>Coverage Guard</span>
          {verdict?.overall === "red" && (
            <span
              data-testid={`${testid || "coverage-guard-badge"}-count`}
              className={`ml-0.5 inline-flex items-center justify-center rounded-full bg-rose-600 px-1 font-semibold text-white ${trigger.countBadge}`}
            >
              {verdict.perRule.filter(
                (p) => !p.unsupported
                  && (p.status === "under" || p.status === "risk"),
              ).length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 overflow-hidden border-slate-200"
        data-testid={`${testid || "coverage-guard-badge"}-popover`}
      >
        <div className={`px-3.5 py-2.5 border-b ${tone.bg} ${tone.border}`}>
          <div className={`text-sm font-semibold ${tone.text}`}>
            {heading}
          </div>
          <div className="text-[11px] text-slate-600 mt-0.5">
            {(verdict?.overall === "grey" || !verdict?.overall)
              && (verdict?.unsupportedRulesCount || 0) > 0
              ? "Rules exist but they use fields Coverage Guard cannot check yet. Each rule explains why below."
              : tone.sub}
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto px-3.5 py-2.5 space-y-2">
          {(!verdict || verdict.totalEvaluatedCount === 0)
            && verdict?.unsupportedRulesCount === 0 && (
            <div className="text-[12px] text-slate-500 py-1.5">
              {emptyHint || "No Coverage Guard rules configured yet."}
            </div>
          )}
          {verdict?.perRule?.map((p, idx) => {
            const t = ROW_TONE[p.status] || ROW_TONE["not-fully-checked"];
            const tw = p.timeWindow
              || ((p.rule.coverage_period === "time_window"
                    && p.rule.start_time && p.rule.end_time)
                  ? { start: p.rule.start_time, end: p.rule.end_time }
                  : null);
            return (
              <div
                key={p.rule.coverage_id || idx}
                data-testid={`coverage-guard-row-${p.rule.coverage_id || idx}`}
                className={`rounded-md border ${t.border} ${t.bg} px-2.5 py-1.5`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-medium text-slate-800 truncate">
                    {p.rule.name}
                  </div>
                  <span className={`text-[10px] font-semibold ${t.text} shrink-0`}>
                    {t.label}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {p.rule.category_name || p.rule.category_id}
                  {p.rule.team_name && (
                    <span> · {p.rule.team_name}</span>
                  )}
                  {p.rule.min_headcount != null && (
                    <span> · min {p.rule.min_headcount}</span>
                  )}
                  {p.rule.min_coverage_pct != null && (
                    <span> · min {p.rule.min_coverage_pct}%</span>
                  )}
                  {tw && (
                    <span
                      data-testid={`coverage-guard-row-window-${p.rule.coverage_id || idx}`}
                    > · {tw.start} to {tw.end}</span>
                  )}
                </div>
                {p.reason && (
                  <div className="text-[10px] text-slate-500 mt-1 italic">
                    {p.reason}
                  </div>
                )}
                {!p.unsupported && p.status !== "ok" && p.perDay && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {p.perDay
                      .filter((d) => d.in_window
                        && (d.status === "under" || d.status === "risk"))
                      .slice(0, 8)
                      .map((d) => {
                        const dt = ROW_TONE[d.status];
                        const label = dayLabels
                          ? (dayLabels[d.day] || `D${d.day + 1}`)
                          : DOW[d.dow];
                        return (
                          <span
                            key={`${p.rule.coverage_id}-${d.day || d.date}`}
                            className={`text-[10px] px-1.5 py-0.5 rounded ${dt.bg} ${dt.text} ${dt.border} border`}
                          >
                            {label}
                            {d.assigned_headcount != null && (
                              <span> · {d.assigned_headcount}</span>
                            )}
                            {p.rule.min_headcount != null && (
                              <span>/{p.rule.min_headcount}</span>
                            )}
                          </span>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {onOpenSettings && (
          <div className="border-t border-slate-100 px-3 py-2 bg-white">
            <button
              type="button"
              data-testid={`${testid || "coverage-guard-badge"}-manage-link`}
              onClick={() => { setOpen(false); onOpenSettings(); }}
              className="text-[12px] font-medium text-[#1D4ED8] hover:underline"
            >
              Manage Coverage Guard
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default CoverageGuardBadge;
